/**
 * Paid headcount and FTE by month.
 *
 * A payroll measure on the finance basis, not an establishment measure — it
 * covers its own full history and does not move with the basis toggle, the
 * period picker or the range slicer. Those controls address point-in-time dates;
 * this series is monthly payroll runs, a different grain entirely, so it
 * carries its OWN window control (12m / 24m / All plus the brush) and says so.
 *
 * Dated by its payroll month — the newest month Finance has closed — never by
 * the frame it rides on. The paid series is organisation-wide and never
 * withheld: a null is a month with no figure, drawn as a gap and named as such,
 * never as a zero.
 *
 * Fifty-five months at once is a smear, so the default view is the last 24 and
 * the readout underneath always names the window actually drawn — a zoomed
 * chart must never read as the whole history.
 */
import React, { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Brush,
} from 'recharts';
import { cn } from '@/lib/utils';
import { LegendSwatch, PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
// One formatter for both time-series charts on this page, so the thousands
// separators cannot drift apart between them.
import { formatTimeSeriesValue } from './TrendPanel';
import {
  ACCENT,
  AXIS,
  GRID,
  fmtMonth,
  fmtNum,
  panelDateLabel,
  type DefLookup,
  type WorkforcePanels,
  type WorkforcePayRow,
} from './apiContract';

interface PayrollPanelProps {
  panels: WorkforcePanels | null;
  loading: boolean;
  def: DefLookup;
}

const HEADCOUNT_COLOR = '#2E6FD9';
const FTE_COLOR = '#0B7F72';
const FTE_NAME = 'FTE (capped)';

/** Months shown by default — two years reads as a shape, four reads as noise. */
const DEFAULT_MONTHS = 24;

const PRESETS: { id: string; label: string; months: number | null }[] = [
  { id: '12m', label: '12m', months: 12 },
  { id: '24m', label: '24m', months: DEFAULT_MONTHS },
  { id: 'all', label: 'All', months: null },
];

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/** "2026-07" -> "Jul 26" — the axis is tight once the window is short. */
const shortMonth = (ym: string) => {
  const full = fmtMonth(ym);
  return full.length > 4 ? `${full.slice(0, 3)} ${full.slice(-2)}` : full;
};

const MILLIONS = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtRandMillions = (v: number) => `R${MILLIONS.format(v / 1_000_000)} million`;

/** What stands in for a month's missing figure. The paid series is never withheld, so a
 *  null is a month with no figure; the withheld wording is kept only for a row that says so. */
const gapText = (row: WorkforcePayRow) =>
  row.suppressed ? 'withheld to protect small groups' : 'no figure for this month';

function payFigure(row: WorkforcePayRow, key: 'headcount' | 'fte'): string {
  const v = row[key];
  if (v === null || v === undefined) return gapText(row);
  return key === 'fte' ? formatTimeSeriesValue(v, { decimals: true }) : fmtNum(v);
}

const TipLine: React.FC<{ label: string; value: ReactNode; color?: string }> = ({ label, value, color }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="inline-flex items-center gap-1.5 text-gray-600">
      {color ? <span className="inline-block h-2 w-2 rounded-sm" style={{ background: color }} /> : null}
      {label}
    </span>
    <span className="font-semibold tabular-nums text-heritage-blue">{value}</span>
  </div>
);

interface PayTipProps {
  active?: boolean;
  payload?: { payload?: WorkforcePayRow }[];
}

/** The month's figures from the row itself, so a gap month names its gap and the
 *  recurring staff cost rides along where the month carries it. */
const PayTip: React.FC<PayTipProps> = ({ active, payload }) => {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  return (
    <div className="min-w-[220px] rounded border border-gray-200 bg-white p-2.5 text-[12px] shadow-lg">
      <div className="mb-1 font-semibold text-heritage-blue">{fmtMonth(row.month)}</div>
      <div className="space-y-0.5">
        <TipLine label="People paid" color={HEADCOUNT_COLOR} value={payFigure(row, 'headcount')} />
        <TipLine label={FTE_NAME} color={FTE_COLOR} value={payFigure(row, 'fte')} />
        {typeof row.cost === 'number' ? (
          <TipLine label="Recurring staff cost" value={fmtRandMillions(row.cost)} />
        ) : null}
      </div>
    </div>
  );
};

/** A readout figure, or the words for its gap. */
const Readout: React.FC<{ row: WorkforcePayRow; field: 'headcount' | 'fte'; text: string }> = ({
  row,
  field,
  text,
}) =>
  row[field] === null || row[field] === undefined ? (
    <span className="text-gray-400">{gapText(row)}</span>
  ) : (
    <b className="text-heritage-blue">{text}</b>
  );

export const PayrollPanel: React.FC<PayrollPanelProps> = ({ panels, loading, def }) => {
  const rows = useMemo(() => panels?.pay ?? [], [panels]);
  const last = Math.max(0, rows.length - 1);

  // null = "the default window", so a new payroll load re-anchors on the most
  // recent months instead of holding a window the data may no longer contain.
  const [view, setView] = useState<{ start: number; end: number } | null>(null);
  useEffect(() => {
    setView(null);
  }, [rows.length]);

  const defaultStart = Math.max(0, rows.length - DEFAULT_MONTHS);
  const vStart = view ? clamp(view.start, 0, last) : defaultStart;
  const vEnd = view ? clamp(view.end, vStart, last) : last;
  const viewRows = rows.slice(vStart, vEnd + 1);
  const shown = viewRows.length;
  const lastInView = viewRows.length ? viewRows[viewRows.length - 1] : null;

  const activePreset = useMemo(() => {
    if (!rows.length) return null;
    if (vStart === 0 && vEnd === last) return 'all';
    if (vEnd !== last) return null;
    if (shown === 12) return '12m';
    if (shown === DEFAULT_MONTHS) return '24m';
    return null;
  }, [rows.length, vStart, vEnd, last, shown]);

  // A four-year window reads as years; a short one needs months, or the axis
  // ends up with a single January tick and no sense of scale.
  const dense = shown > 30;
  const ticks = useMemo(() => {
    if (!viewRows.length) return undefined;
    if (dense) {
      const jans = viewRows.filter((r) => r.month.endsWith('-01')).map((r) => r.month);
      return jans.length ? jans : undefined;
    }
    const step = Math.max(1, Math.ceil(viewRows.length / 6));
    return viewRows.filter((_, i) => i % step === 0).map((r) => r.month);
  }, [viewRows, dense]);

  return (
    <PanelShell
      title="Paid headcount and FTE by month"
      defKey="paid_headcount_fte"
      def={def}
      window={
        rows.length
          ? `${panelDateLabel(panels, 'pay')} · everyone Finance paid, ${fmtMonth(rows[0].month)} – ${fmtMonth(rows[last].month)} · full-time equivalents capped at one per person. Its own month range — independent of the basis toggle, the period picker and the range slicer.`
          : 'Everyone Finance paid, by payroll month · full-time equivalents capped at one per person. Its own month range — independent of the basis toggle, the period picker and the range slicer.'
      }
      right={
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <LegendSwatch color={HEADCOUNT_COLOR} label="People paid" defKey="paid_headcount" def={def} />
          <LegendSwatch color={FTE_COLOR} label={FTE_NAME} defKey="paid_fte" def={def} />
          {rows.length ? (
            <div className="flex items-center gap-1" role="group" aria-label="Months shown">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={activePreset === p.id}
                  disabled={p.months !== null && p.months > rows.length}
                  onClick={() =>
                    setView({
                      start: p.months === null ? 0 : Math.max(0, rows.length - p.months),
                      end: last,
                    })
                  }
                  className={cn(
                    'rounded-full border px-2.5 py-0.5 text-[11px] transition-colors',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-future-blue',
                    activePreset === p.id
                      ? 'border-heritage-blue bg-heritage-blue text-white'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                    p.months !== null && p.months > rows.length && 'cursor-not-allowed opacity-40',
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      }
      footnote="Updated when Finance closes a payroll month, so it moves on Finance's cycle rather than with the establishment. A month with no published figure is drawn as a gap, never as a zero. Finance's full-time equivalents use cost to company less bonus and commission, which is why no other payroll report reproduces them."
    >
      {loading && !panels ? (
        <PanelSkeleton height={260} />
      ) : !panels ? (
        <PanelUnavailable what="Paid headcount and FTE" />
      ) : !rows.length ? (
        <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
          Paid headcount and FTE is not available for this date.
        </div>
      ) : (
        <>
          <div style={{ width: '100%', height: 262 }}>
            <ResponsiveContainer>
              <LineChart data={rows} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis
                  dataKey="month"
                  ticks={ticks}
                  tickFormatter={(v: string) => (dense ? v.slice(0, 4) : shortMonth(v))}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: AXIS }}
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                  tickFormatter={(v: number) => fmtNum(v)}
                />
                {/* filterNull off, so a gap month still opens its tooltip and names the gap. */}
                <RechartsTooltip content={<PayTip />} filterNull={false} />
                <Line
                  type="monotone"
                  dataKey="headcount"
                  name="People paid"
                  stroke={HEADCOUNT_COLOR}
                  strokeWidth={2}
                  connectNulls={false}
                  dot={false}
                  activeDot={{ r: 4.5, stroke: '#ffffff', strokeWidth: 2 }}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="fte"
                  name={FTE_NAME}
                  stroke={FTE_COLOR}
                  strokeWidth={2}
                  connectNulls={false}
                  dot={false}
                  activeDot={{ r: 4.5, stroke: '#ffffff', strokeWidth: 2 }}
                  isAnimationActive={false}
                />
                <Brush
                  dataKey="month"
                  height={20}
                  travellerWidth={8}
                  stroke={ACCENT}
                  startIndex={vStart}
                  endIndex={vEnd}
                  tickFormatter={(v: string) => shortMonth(String(v))}
                  onChange={(next: { startIndex?: number; endIndex?: number }) =>
                    setView({ start: next.startIndex ?? 0, end: next.endIndex ?? last })
                  }
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            {lastInView ? (
              <span className="text-[12.5px] tabular-nums text-gray-600">
                {fmtMonth(lastInView.month)}: people paid{' '}
                <Readout row={lastInView} field="headcount" text={fmtNum(lastInView.headcount)} /> · FTE on
                the capped conversion{' '}
                <Readout
                  row={lastInView}
                  field="fte"
                  text={fmtNum(lastInView.fte === null ? null : Math.round(lastInView.fte))}
                />
                {typeof lastInView.cost === 'number' ? (
                  <>
                    {' '}
                    · recurring staff cost <b className="text-heritage-blue">{fmtRandMillions(lastInView.cost)}</b>
                  </>
                ) : null}
              </span>
            ) : (
              <span />
            )}
            <span className="text-[11px] tabular-nums text-gray-500">
              Showing {fmtMonth(viewRows[0]?.month)} – {fmtMonth(lastInView?.month)} · {shown} of{' '}
              {rows.length} month{rows.length === 1 ? '' : 's'}
            </span>
          </div>
        </>
      )}
    </PanelShell>
  );
};
